// client/src/components/scheduling/MatchProposalModal.js
import React, { useState, useMemo, useEffect } from 'react'
import { format } from 'date-fns'
import { formatUtcToLocal } from '../../utils/format'
import './MatchProposalModal.css'
import RequireAvailabilityModal from './RequireAvailabilityModal'

const PROPOSAL_FMT = "EEEE, MMM d 'at' h:mm a"
const MATCH_DURATION_MINUTES = 90
const TIME_INTERVAL_MINUTES = 30
const STUCK_REQUEST_TIMEOUT_MS = 20000 // 20 seconds

export default function MatchProposalModal({
  isOpen,
  onClose,
  opponent,
  proposedTime,
  availabilityStart = null,
  availabilityEnd = null,
  leagueId,
  onConfirm,
  directProposal = false,
}) {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [expirationOption, setExpirationOption] = useState('none')
  const [selectedTime, setSelectedTime] = useState(proposedTime)
  const [showStuckWarning, setShowStuckWarning] = useState(false)
  const [showAvailabilityModal, setShowAvailabilityModal] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')

  // Generate time options within the availability window
  const timeOptions = useMemo(() => {
    if (!availabilityStart || !availabilityEnd) return []

    const start = new Date(availabilityStart)
    const end = new Date(availabilityEnd)
    const options = []

    const matchDurationMs = MATCH_DURATION_MINUTES * 60 * 1000
    const intervalMs = TIME_INTERVAL_MINUTES * 60 * 1000

    // Generate intervals within the window
    let current = new Date(start)
    while (current.getTime() + matchDurationMs <= end.getTime()) {
      options.push({
        value: current.toISOString(),
        label: formatUtcToLocal(current.toISOString(), 'h:mm a'),
      })
      current = new Date(current.getTime() + intervalMs)
    }

    return options
  }, [availabilityStart, availabilityEnd])

  // Reset state when modal opens or proposedTime changes
  useEffect(() => {
    if (isOpen) {
      setIsSubmitting(false) // Reset submission state on open
      setShowStuckWarning(false)
      setShowAvailabilityModal(false) // Reset availability modal state
      setErrorMessage('') // Clear any error messages
      setExpirationOption('none')
      const defaultTime = new Date()
      defaultTime.setMinutes(0, 0, 0)
      defaultTime.setHours(defaultTime.getHours() + 1)
      const fallbackTime = defaultTime.toISOString()
      // If we have time options and proposedTime is not in the list, use the first option
      // Otherwise use proposedTime
      if (timeOptions.length > 0) {
        const proposedTimeInOptions = timeOptions.some(
          (opt) => opt.value === proposedTime,
        )
        setSelectedTime(
          proposedTimeInOptions ? proposedTime : timeOptions[0].value,
        )
      } else {
        setSelectedTime(proposedTime || fallbackTime)
      }
    }
  }, [isOpen, proposedTime, timeOptions])

  // Watchdog timer to detect stuck requests
  useEffect(() => {
    if (!isSubmitting || !isOpen) {
      setShowStuckWarning(false)
      return
    }

    const timer = setTimeout(() => {
      setShowStuckWarning(true)
    }, STUCK_REQUEST_TIMEOUT_MS)

    return () => clearTimeout(timer)
  }, [isSubmitting, isOpen])

  const formattedTime = useMemo(() => {
    if (isNaN(Date.parse(selectedTime))) return selectedTime
    return formatUtcToLocal(selectedTime, PROPOSAL_FMT)
  }, [selectedTime])

  // Show availability window if provided
  const hasAvailabilityWindow = availabilityStart && availabilityEnd
  const isDirectProposal = directProposal || !hasAvailabilityWindow
  const formattedAvailabilityWindow = useMemo(() => {
    if (!hasAvailabilityWindow) return null
    const start = formatUtcToLocal(availabilityStart, 'h:mm a')
    const end = formatUtcToLocal(availabilityEnd, 'h:mm a')
    return `${start} – ${end}`
  }, [availabilityStart, availabilityEnd, hasAvailabilityWindow])

  // Generate expiration options (relative to selected time)
  // Policy: Support "None" and "24 hours before match" only
  // Expiration is a lead-time cutoff: expiresAt = startAt - expiryLead
  const expirationOptions = useMemo(() => {
    if (!selectedTime || isNaN(Date.parse(selectedTime))) return []

    const proposed = new Date(selectedTime)
    const now = new Date()
    const hoursUntilMatch = (proposed - now) / (1000 * 60 * 60)

    const options = [
      {
        value: 'none',
        label: 'No expiration',
        expiresAt: null,
      },
    ]

    // Only show "24 hours before match" if match is more than 24 hours away
    if (hoursUntilMatch > 24) {
      options.push({
        value: '24hours',
        label: '24 hours before match',
        expiresAt: new Date(proposed.getTime() - 24 * 60 * 60 * 1000),
      })
    }

    return options
  }, [selectedTime])

  // Calculate the selected expiration date
  const selectedExpirationDate = useMemo(() => {
    const option = expirationOptions.find(
      (opt) => opt.value === expirationOption,
    )
    return option?.expiresAt || null
  }, [expirationOption, expirationOptions])

  // Format selected expiration for display
  const formattedExpiration = useMemo(() => {
    if (!selectedExpirationDate) return ''
    return formatUtcToLocal(selectedExpirationDate.toISOString(), PROPOSAL_FMT)
  }, [selectedExpirationDate])

  const formattedDateTimeInput = useMemo(() => {
    if (!selectedTime || isNaN(Date.parse(selectedTime))) return ''
    return format(new Date(selectedTime), "yyyy-MM-dd'T'HH:mm")
  }, [selectedTime])

  const minDateTimeInput = useMemo(() => {
    return format(new Date(), "yyyy-MM-dd'T'HH:mm")
  }, [])

  // Handler to reset stuck state and allow retry
  const handleForceReset = () => {
    setIsSubmitting(false)
    setShowStuckWarning(false)
    // Modal stays open, user can try again
  }

  if (!isOpen || !opponent) return null

  const handleConfirm = async () => {
    if (isSubmitting) return

    const selectedDate = new Date(selectedTime)
    if (isNaN(selectedDate.getTime())) {
      setErrorMessage('Please choose a valid date and time.')
      return
    }
    if (selectedDate <= new Date()) {
      setErrorMessage('Proposed time must be in the future.')
      return
    }

    // Verify the selected option exists in current options
    const selectedOption = expirationOptions.find(
      (opt) => opt.value === expirationOption,
    )

    if (!selectedOption) {
      alert('Please select a valid expiration option.')
      return
    }

    // Validate "No expiration" option - should have null expiration
    if (expirationOption === 'none') {
      if (selectedExpirationDate !== null) {
        alert(
          'Invalid state: No expiration selected but expiration date exists.',
        )
        return
      }
    }

    // For non-none options (e.g., "24 hours before match"), validate expiration date
    if (expirationOption !== 'none') {
      if (!selectedExpirationDate) {
        alert('Please select a valid expiration time.')
        return
      }
      if (selectedExpirationDate <= new Date()) {
        alert('Expiration must be in the future.')
        return
      }
      if (selectedExpirationDate >= new Date(selectedTime)) {
        alert('Expiration must be before the proposed match time.')
        return
      }
    }

    setIsSubmitting(true)
    try {
      await onConfirm(
        opponent.id,
        selectedTime,
        leagueId,
        selectedExpirationDate ? selectedExpirationDate.toISOString() : null,
        null,
        isDirectProposal,
      )
      // Close modal immediately on success - don't wait for data reload
      onClose()
    } catch (error) {
      console.error('Error confirming match proposal:', error)

      // Check if this is the availability requirement error
      // Error structure from backend: { success: false, error: 'AVAILABILITY_REQUIRED_FOR_PROPOSAL', ... }
      // But BaseService wraps it, so we check the error object
      if (
        error.error === 'AVAILABILITY_REQUIRED_FOR_PROPOSAL' ||
        error.response?.data?.error === 'AVAILABILITY_REQUIRED_FOR_PROPOSAL'
      ) {
        // Show availability modal instead of generic error
        setShowAvailabilityModal(true)
        // Keep the proposal modal open so user can retry after adding availability
      } else {
        // For other errors, show user-facing error message
        setErrorMessage(
          'Failed to send proposal. Please try again or contact support if the problem persists.',
        )
      }

      setIsSubmitting(false) // Only reset on error
    }
    // Note: Don't reset isSubmitting on success - modal will close
  }

  return (
    <>
      <div className="match-proposal-modal-overlay" onClick={onClose}>
        <div
          className="match-proposal-modal-content"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="match-proposal-modal-header">
            <h3>Propose Match</h3>
            <button className="match-proposal-close-button" onClick={onClose}>
              ×
            </button>
          </div>

          <div className="match-proposal-modal-body">
            <div className="proposal-details">
              <div className="opponent-info">
                <h4>Opponent</h4>
                <p className="opponent-name">
                  {opponent.first_name} {opponent.last_name}
                </p>
              </div>

              <div className="match-time">
                <h4>Proposed Time</h4>
                {!isDirectProposal &&
                hasAvailabilityWindow &&
                timeOptions.length > 1 ? (
                  <>
                    <p className="availability-window text-muted mb-2">
                      Availability: {formattedAvailabilityWindow}
                    </p>
                    <label
                      htmlFor="match-time-select"
                      className="visually-hidden"
                    >
                      Select match start time
                    </label>
                    <select
                      id="match-time-select"
                      className="form-select"
                      aria-label="Select match start time"
                      value={selectedTime}
                      onChange={(e) => {
                        setSelectedTime(e.target.value)
                        setErrorMessage('')
                      }}
                      disabled={isSubmitting}
                    >
                      {timeOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </>
                ) : isDirectProposal ? (
                  <>
                    <label htmlFor="match-time-input" className="form-label">
                      Select match date and time
                      <small className="text-muted ms-2">
                        ({Intl.DateTimeFormat().resolvedOptions().timeZone})
                      </small>
                    </label>
                    <input
                      id="match-time-input"
                      type="datetime-local"
                      className="form-control"
                      value={formattedDateTimeInput}
                      min={minDateTimeInput}
                      onChange={(e) => {
                        const nextValue = e.target.value
                        if (!nextValue) {
                          setSelectedTime('')
                          return
                        }
                        const parsed = new Date(nextValue)
                        setSelectedTime(parsed.toISOString())
                        setErrorMessage('')
                      }}
                      disabled={isSubmitting}
                    />
                  </>
                ) : (
                  <p className="proposed-time">{formattedTime}</p>
                )}
              </div>

              <div className="expiration-selection">
                <h4>Proposal Expiration</h4>
                <div className="expiration-options">
                  {expirationOptions.map((option) => (
                    <label
                      key={option.value}
                      className={`expiration-option ${
                        expirationOption === option.value ? 'selected' : ''
                      }`}
                    >
                      <input
                        type="radio"
                        name="expiration"
                        value={option.value}
                        checked={expirationOption === option.value}
                        onChange={(e) => setExpirationOption(e.target.value)}
                      />
                      <span className="option-label">{option.label}</span>
                    </label>
                  ))}
                </div>

                {formattedExpiration && expirationOption !== 'none' && (
                  <p
                    className="expiration-preview"
                    style={{
                      margin: '8px 0 0 0',
                      fontSize: '0.9rem',
                      color: '#007bff',
                      fontStyle: 'italic',
                    }}
                  >
                    Expires: {formattedExpiration}
                  </p>
                )}
              </div>

              <div className="proposal-note">
                <p className="text-muted">
                  This will send a match proposal to {opponent.first_name}.
                </p>
              </div>
            </div>
          </div>

          <div className="match-proposal-modal-footer">
            {errorMessage && (
              <div className="alert alert-danger mt-3">
                <div className="d-flex align-items-center gap-2">
                  <span>❌</span>
                  <div className="flex-grow-1">{errorMessage}</div>
                </div>
              </div>
            )}
            {showStuckWarning && (
              <div className="alert alert-warning mt-3">
                <div className="d-flex align-items-center gap-2">
                  <span>⚠️</span>
                  <div className="flex-grow-1">
                    This is taking longer than expected. The request may be
                    stuck.
                  </div>
                </div>
                <div className="d-flex gap-2 mt-2">
                  <button
                    onClick={handleForceReset}
                    className="btn btn-sm btn-warning"
                  >
                    Try Again
                  </button>
                  <button
                    onClick={() => window.location.reload()} // Full page reload to clear any corrupted state
                    className="btn btn-sm btn-secondary"
                  >
                    Refresh Page
                  </button>
                </div>
              </div>
            )}
            <button
              className="btn btn-secondary"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              className="btn btn-primary"
              onClick={handleConfirm}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Sending...' : 'Send Proposal'}
            </button>
          </div>
        </div>
      </div>

      {/* Availability requirement modal */}
      <RequireAvailabilityModal
        show={showAvailabilityModal}
        onClose={() => {
          setShowAvailabilityModal(false)
          // Reset submission state so user can try again
          setIsSubmitting(false)
        }}
        leagueId={leagueId}
      />
    </>
  )
}
